import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import type { FC, ReactNode } from "react";
import React, { useEffect, useState } from "react";

export interface ProgressIndicatorProps {
  img?: string;
  loadTime?: number;
  percent?: number;
  controlled?: boolean;
  indeterminate?: boolean;
  complete?: boolean;
  children?: ReactNode;
  onComplete?: () => void;
}

const barAnimation = keyframes`
  0% {
    width: 0;
  }
  100% {
    width: 100%;
  }
`;

const shimmer = keyframes`
  to {
    background-position-x: -200%;
  }
`;

const indeterminateAnimation = keyframes`
  0% {
    width: 0%;
  }
  20% {
    width: 50%;
  }
  50% {
    width: 70%;
  }
  80% {
    width: 82%;
  }
  100% {
    width: 85%;
  }
`;

const Container = styled.div`
  display: block;
  width: clamp(140px, 100%, 387px);
  margin: 0 auto;

  --bar-height: 9px;
  --bar-color: var(
    --gradient-sunrise,
    linear-gradient(90deg, #80aeff 0.36%, #f7bde6 52.67%, #ffbd70 99.65%)
  );
  --bar-background: var(--color-cream-dark, #e8e4df);
  --bar-radius: 2em;
  --bar-border-width: 2px;
`;

const ProgressWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ImageContainer = styled.div<{ loaded: boolean; imgUrl: string }>`
  width: 100%;
  margin: 0 auto;
  aspect-ratio: var(--image-aspect-ratio, 327/274);
  background-image: url(${({ imgUrl }) => imgUrl});
  background-size: cover;
  background-position: center;
  display: block;
  opacity: ${({ loaded }) => (loaded ? 1 : 0)};
  transform: translateY(${({ loaded }) => (loaded ? "0" : "-1em")});
  transition:
    opacity 0.3s ease-in-out,
    transform 0.5s ease-out;
`;

const Info = styled.div`
  margin: var(--space-xs, 0.5rem) auto var(--space-md, 1rem);
  text-align: center;
`;

const Bar = styled.div`
  width: min(100%, 180px);
  border-radius: var(--bar-radius);
  height: calc(var(--bar-height) + var(--bar-border-width) * 2);
  background: var(--bar-color);
  margin: 0 auto;
  position: relative;
  padding: var(--bar-border-width);
  box-sizing: border-box;
`;

const BarTrack = styled.div`
  width: 100%;
  height: var(--bar-height);
  background: var(--bar-background);
  border-radius: var(--bar-radius);
  position: relative;
  overflow: hidden;
`;

const BarFill = styled.div<{
  loadTime: number;
  controlled: boolean;
  percent: number;
  indeterminate: boolean;
  complete: boolean;
}>`
  height: 100%;
  background: var(--bar-color);
  border-radius: var(--bar-radius);
  background-size: 180px 100%;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: linear-gradient(
      100deg,
      transparent 40%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 60%
    );
    background-size: 200% 100%;
    background-position-x: 180%;
    animation: ${shimmer} 2s ease-in-out infinite;
  }

  ${({ controlled, loadTime, percent, indeterminate, complete }) => {
    if (indeterminate) {
      if (complete) {
        return css`
          width: 100%;
          transition: width 0.3s ease-out;
        `;
      }
      return css`
        animation: ${indeterminateAnimation} ${loadTime}ms ease-out forwards;
      `;
    }
    if (controlled) {
      return css`
        width: ${percent * 100}%;
        transition: width 0.3s ease-in-out;
      `;
    }
    return css`
      animation: ${barAnimation} ${loadTime}ms ease-in-out forwards;
    `;
  }}
`;

const ProgressIndicator: FC<ProgressIndicatorProps> = ({
  img,
  loadTime = 2000,
  percent = 0,
  controlled = false,
  indeterminate = false,
  complete = false,
  children,
  onComplete
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (img) {
      const image = new Image();
      image.src = img;
      image.onload = () => setImageLoaded(true);
    }
  }, [img]);

  useEffect(() => {
    if (!controlled && !indeterminate && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, loadTime);
      return () => clearTimeout(timer);
    }
  }, [controlled, indeterminate, loadTime, onComplete]);

  useEffect(() => {
    if (controlled && percent >= 1 && onComplete) {
      onComplete();
    }
  }, [controlled, percent, onComplete]);

  useEffect(() => {
    if (indeterminate && complete && onComplete) {
      const timer = setTimeout(() => {
        onComplete();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [indeterminate, complete, onComplete]);

  return (
    <Container>
      <ProgressWrapper>
        {img && <ImageContainer loaded={imageLoaded} imgUrl={img} />}
        <Info>{children}</Info>
        <Bar>
          <BarTrack>
            <BarFill
              loadTime={loadTime}
              controlled={controlled}
              percent={percent}
              indeterminate={indeterminate}
              complete={complete}
            />
          </BarTrack>
        </Bar>
      </ProgressWrapper>
    </Container>
  );
};

export default ProgressIndicator;
